import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronRight, ArrowRight, ShieldCheck, SlidersHorizontal, ListFilter } from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { useAuth } from "../contexts/AuthProvider"
import Header from "../components/Header"
import Footer from "../components/Footer"
import figmaLogo from "../assets/figma.webp"
import riotLogo from "../assets/riot.webp"
import scaleLogo from "../assets/sca;e.webp"
import slbLogo from "../assets/slb.webp"
import spluckLogo from "../assets/spluck.webp"
import travleLogo from "../assets/travle.webp"
import webflowLogo from "../assets/webflow.webp"
import FreeTrialModal from "../components/FreeTrialModal"
import CalBookingModal from "../components/CalBookingModal"

const FONT = '"Plus Jakarta Sans", sans-serif'

const TABS = [
  { label: "Shortlist",   image: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1777565423/Screenshot_2026-04-30_at_9.40.04_PM_yo30qq.png" },
  { label: "Communicate", image: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1773662046/Screenshot_2026-03-16_at_5.23.27_PM_knuthw.png" },
  { label: "Schedule",    image: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1781415015/ChatGPT_Image_Jun_14_2026_10_29_33_AM_tyk4yk.png" },
  { label: "Decide",      image: "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1777565524/Screenshot_2026-04-30_at_9.41.48_PM_f5zebg.png" },
]

const LOGOS = [
  { src: riotLogo,    alt: "Riot Games" },
  { src: scaleLogo,   alt: "Scale" },
  { src: slbLogo,     alt: "SLB" },
  { src: spluckLogo,  alt: "Splunk" },
  { src: travleLogo,  alt: "Travelers" },
  { src: figmaLogo,   alt: "Figma" },
  { src: webflowLogo, alt: "Webflow" },
]

const JOB_BOARDS = [
  { name: "LinkedIn",     color: "#0A66C2", bg: "#EBF3FB", top: "2%",  left: "34%" },
  { name: "Indeed",       color: "#2557A7", bg: "#EBF0FA", top: "18%", left: "76%" },
  { name: "Monster",      color: "#6D28D9", bg: "#EDE9FE", top: "62%", left: "80%" },
  { name: "ZipRecruiter", color: "#047857", bg: "#D1FAE5", top: "80%", left: "33%" },
  { name: "Glassdoor",    color: "#0CAA41", bg: "#DCFCE7", top: "62%", left: "4%"  },
  { name: "Wellfound",    color: "#E85D04", bg: "#FEE9D7", top: "18%", left: "6%"  },
]

export default function ResolutHome() {
  const navigate = useNavigate()
  const { user, updateUser } = useAuth()
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false)
  const [showCalModal, setShowCalModal] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  )

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)")
    setIsDesktop(mq.matches)
    const handler = e => setIsDesktop(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const bookDemo = () => setShowCalModal(true)

  return (
    <div className="flex flex-col min-h-screen" style={{ background: "#FDFAF8" }}>
      <Header user={user} onUserChange={updateUser} />

      {/* ── HERO + TABS — hero-bg.webp background, tabs inside ── */}
      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: "url(/hero-bg.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundColor: "#FDFAF8",
        }}
      >
        <div className="absolute inset-0 pointer-events-none" />

        {/* Text block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 flex flex-col items-center text-center px-4 pt-24 pb-10"
        >
          {/* Gradient badge */}
          <div className="badge-gradient p-px rounded-full mb-8 inline-flex hover:opacity-80 transition-opacity cursor-default">
            <div className="flex items-center gap-1.5 bg-white rounded-full px-1.5 py-1 pr-3">
              <span className="bg-zinc-900 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ fontFamily: FONT }}>NEW</span>
              <span className="text-[12px] font-medium text-zinc-700" style={{ fontFamily: FONT }}>AI interviews are now live</span>
            </div>
          </div>

          {/* Headline */}
          <h1
            className="text-[28px] leading-tight sm:text-[40px] lg:text-[54px] lg:leading-[1.1] font-semibold tracking-[-0.03em] text-zinc-900 mb-4 text-center"
            style={{ fontFamily: FONT }}
          >
            The AI-powered ATS<br />for modern hiring teams
          </h1>

          {/* Subheading */}
          <p
            className="text-sm sm:text-[15px] lg:text-base font-normal leading-relaxed max-w-sm sm:max-w-md mb-9 text-center"
            style={{ fontFamily: FONT, color: "#444444" }}
          >
            Source, screen, interview, and decide — all in one place.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
            <button
              onClick={() => setShowFreeTrialModal(true)}
              className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-700 text-white text-[13px] font-bold tracking-[.04em] px-6 py-3 rounded-lg transition-all duration-200 hover:-translate-y-px shadow-lg shadow-zinc-900/20 cursor-pointer"
              style={{ fontFamily: FONT }}
            >
              Start for free <ChevronRight size={15} />
            </button>
            <button
              onClick={bookDemo}
              className="inline-flex items-center gap-1.5 bg-white hover:bg-zinc-50 text-zinc-800 text-[13px] font-bold tracking-[.04em] px-6 py-3 rounded-lg border border-zinc-200 hover:border-zinc-300 transition-all duration-200 cursor-pointer"
              style={{ fontFamily: FONT }}
            >
              Book a Demo
            </button>
          </div>

          <p className="text-[11px] text-zinc-400" style={{ fontFamily: FONT }}>
            No credit card · No seat limits · Free forever
          </p>
        </motion.div>

        {/* Tab pills + screenshot — inside hero, on hero-bg.webp */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 pb-0 mt-5 sm:mt-8">
          {/* Tab pills */}
          <div className="flex justify-center mb-6 sm:mb-10">
            <div className="flex items-center gap-1 bg-white/80 backdrop-blur-md border border-zinc-200/80 rounded-full px-1.5 py-1.5 shadow-sm overflow-x-auto scrollbar-hide max-w-full">
              {TABS.map((tab, i) => (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(i)}
                  className={`px-3.5 py-1.5 sm:px-5 sm:py-2 rounded-full text-[11px] sm:text-[13px] font-semibold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
                    activeTab === i ? "bg-zinc-900 text-white shadow-md" : "text-zinc-600 hover:text-zinc-900"
                  }`}
                  style={{ fontFamily: FONT }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Screenshot card — warm card like anyroundai video block */}
          <div
            className="relative w-full overflow-hidden rounded-[18px] sm:rounded-[22px] lg:rounded-xl bg-[#ece5d8] shadow-[0_16px_42px_rgba(0,0,0,0.12)]"
            style={{ aspectRatio: "1280/698" }}
          >
            {isDesktop ? (
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeTab}
                  src={TABS[activeTab].image}
                  alt={TABS[activeTab].label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </AnimatePresence>
            ) : (
              <img
                src={TABS[activeTab].image}
                alt={TABS[activeTab].label}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            )}
          </div>
        </div>

        {/* ── LOGOS MARQUEE — at bottom of hero section ── */}
        <div className="relative z-10 w-full pt-14 pb-14 mt-14 bg-[#FDF8EE]">
          <p
            className="text-center text-[16px] sm:text-[18px] font-semibold text-zinc-900 mb-8"
            style={{ fontFamily: FONT }}
          >
            Trusted by teams at
          </p>
          <div
            className="relative w-full overflow-hidden"
            style={{ maskImage: "linear-gradient(to right, transparent, black 12%, black 88%, transparent)" }}
          >
            <div className="marquee-track">
              {[...LOGOS, ...LOGOS].map((logo, i) => (
                <img
                  key={i}
                  src={logo.src}
                  alt={logo.alt}
                  loading="eager"
                  className="w-auto flex-shrink-0 object-contain opacity-50 brightness-0 mx-10"
                  style={{ height: 28 }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE: AI Scoring ── */}
      <section className="py-16 md:py-24" style={{ background: "#FDFAF8" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col gap-5">
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-500" style={{ fontFamily: FONT }}>
                AI Candidate Scoring
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 leading-[1.15] tracking-[-0.02em]" style={{ fontFamily: FONT }}>
                Prioritize the best<br />applicants
              </h2>
              <div className="flex flex-col gap-4 mt-1">
                {[
                  { icon: <ShieldCheck size={14} />, text: "Fraud & AI detection filters out spam" },
                  { icon: <SlidersHorizontal size={14} />, text: "Automated scoring based on job requirements" },
                  { icon: <ListFilter size={14} />, text: "Filter candidates with plain-English queries" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-600">
                      {item.icon}
                    </span>
                    <p className="text-[14px] text-zinc-600 leading-snug" style={{ fontFamily: FONT }}>{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <button
                  onClick={bookDemo}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white rounded-lg text-[13px] font-semibold transition-colors cursor-pointer"
                  style={{ fontFamily: FONT }}
                >
                  See it in action <ArrowRight size={14} />
                </button>
              </div>
            </div>

            <div className="overflow-hidden select-none">
              <div className="space-y-4">
                <div className="transform rotate-1 drop-shadow-2xl">
                  <img
                    src="https://res.cloudinary.com/do0q4oj8x/image/upload/v1781692653/ChatGPT_Image_Jun_17_2026_04_06_57_PM_mgoxgf.png"
                    alt="Candidate scoring"
                    className="rounded-xl w-full h-auto"
                    loading="lazy"
                  />
                </div>
                <div className="transform -translate-y-[25%] translate-x-[25%] -rotate-1 -mt-8 drop-shadow-2xl">
                  <img
                    src="https://res.cloudinary.com/do0q4oj8x/image/upload/v1781692653/ChatGPT_Image_Jun_17_2026_04_05_32_PM_amjhjx.png"
                    alt="AI shortlisting"
                    className="rounded-xl w-full h-auto"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE: AI Interview ── */}
      <section className="py-16 md:py-24" style={{ background: "#F5EFE0" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-xl overflow-hidden ring-1 ring-zinc-200 order-2 lg:order-1">
              <img
                src="https://res.cloudinary.com/do0q4oj8x/image/upload/v1781694487/firebug_dtscei.png"
                alt="AI interview platform"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col gap-5 order-1 lg:order-2">
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-500" style={{ fontFamily: FONT }}>
                AI Phone Screen
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 leading-[1.15] tracking-[-0.02em]" style={{ fontFamily: FONT }}>
                Interview candidates<br />at scale
              </h2>
              <div className="flex flex-col gap-4 mt-1">
                {[
                  { icon: <ShieldCheck size={14} />, text: "Works on Zoom, Meet, phone, or our platform" },
                  { icon: <SlidersHorizontal size={14} />, text: "Structured scorecards from every interviewer" },
                  { icon: <ListFilter size={14} />, text: "Detailed report after each round" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-zinc-200 flex items-center justify-center text-zinc-600">
                      {item.icon}
                    </span>
                    <p className="text-[14px] text-zinc-600 leading-snug" style={{ fontFamily: FONT }}>{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <button
                  onClick={() => navigate("/demo/ai-interview")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white rounded-lg text-[13px] font-semibold transition-colors cursor-pointer"
                  style={{ fontFamily: FONT }}
                >
                  Try a mock interview <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE: Job Boards ── */}
      <section className="py-16 md:py-24" style={{ background: "#FDFAF8" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col gap-5">
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-500" style={{ fontFamily: FONT }}>
                Job Boards
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900 leading-[1.15] tracking-[-0.02em]" style={{ fontFamily: FONT }}>
                Reach candidates<br />everywhere, with one click
              </h2>
              <p className="text-[14px] text-zinc-500 leading-relaxed max-w-xs" style={{ fontFamily: FONT }}>
                Post once, reach everywhere — no juggling logins or copy-pasting.
              </p>
              <div className="mt-1">
                <button
                  onClick={bookDemo}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white rounded-lg text-[13px] font-semibold transition-colors cursor-pointer"
                  style={{ fontFamily: FONT }}
                >
                  See it in action <ArrowRight size={14} />
                </button>
              </div>
            </div>

            <div>
              <div className="lg:hidden flex flex-wrap gap-3">
                {JOB_BOARDS.map((b) => (
                  <div
                    key={b.name}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-sm border border-white"
                    style={{ background: b.bg, color: b.color, fontFamily: FONT }}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.color }} />
                    {b.name}
                  </div>
                ))}
              </div>
              <div className="hidden lg:block relative h-[380px] select-none">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-zinc-900 flex items-center justify-center shadow-lg z-10">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  {[
                    { x1: "50%", y1: "50%", x2: "50%", y2: "10%" },
                    { x1: "50%", y1: "50%", x2: "85%", y2: "25%" },
                    { x1: "50%", y1: "50%", x2: "88%", y2: "72%" },
                    { x1: "50%", y1: "50%", x2: "50%", y2: "88%" },
                    { x1: "50%", y1: "50%", x2: "12%", y2: "72%" },
                    { x1: "50%", y1: "50%", x2: "14%", y2: "28%" },
                  ].map((l, i) => (
                    <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#d4d4d8" strokeWidth="1.5" strokeDasharray="4 3" />
                  ))}
                </svg>
                {JOB_BOARDS.map((b) => (
                  <div
                    key={b.name}
                    className="absolute flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold shadow-md border border-white"
                    style={{ background: b.bg, color: b.color, top: b.top, left: b.left, transform: "translate(-50%, -50%)", fontFamily: FONT }}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.color }} />
                    {b.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="py-20 md:py-28" style={{ background: "#F0E9D8" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-center text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-4" style={{ fontFamily: FONT }}>
            Pricing
          </p>
          <h2 className="text-center text-3xl sm:text-4xl font-semibold text-zinc-900 tracking-[-0.02em] mb-14" style={{ fontFamily: FONT }}>
            Pick what you need.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Free */}
            <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden flex flex-col">
              <div className="px-6 pt-6 pb-5 border-b border-zinc-100">
                <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-3" style={{ fontFamily: FONT }}>Get started</p>
                <p className="text-2xl font-bold text-zinc-900 mb-4" style={{ fontFamily: FONT }}>Free</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-zinc-900" style={{ fontFamily: FONT }}>$0</span>
                  <span className="text-[13px] text-zinc-400" style={{ fontFamily: FONT }}>/month</span>
                </div>
              </div>
              <div className="px-6 py-5 flex flex-col gap-3 flex-1">
                {[
                  "Agentic ATS",
                  "500 applications / month",
                  "Advanced screening",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2.5">
                    <ShieldCheck size={13} className="text-zinc-400 flex-shrink-0" />
                    <span className="text-[13px] text-zinc-600" style={{ fontFamily: FONT }}>{f}</span>
                  </div>
                ))}
              </div>
              <div className="px-6 pb-6">
                <button
                  onClick={() => setShowFreeTrialModal(true)}
                  className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white rounded-lg text-[13px] font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  style={{ fontFamily: FONT }}
                >
                  Start for free <ArrowRight size={13} />
                </button>
              </div>
            </div>

            {/* Pro */}
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 shadow-sm overflow-hidden flex flex-col relative">
              <div className="absolute top-4 right-4">
                <span className="bg-white/10 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ fontFamily: FONT }}>POPULAR</span>
              </div>
              <div className="px-6 pt-6 pb-5 border-b border-zinc-800">
                <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-3" style={{ fontFamily: FONT }}>For the curious</p>
                <p className="text-2xl font-bold text-white mb-4" style={{ fontFamily: FONT }}>Pro</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white" style={{ fontFamily: FONT }}>$249</span>
                  <span className="text-[13px] text-zinc-400" style={{ fontFamily: FONT }}>/month</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1.5" style={{ fontFamily: FONT }}>50 AI interviews · scales with usage</p>
              </div>
              <div className="px-6 py-5 flex flex-col gap-3 flex-1">
                {[
                  "Unlimited applicants",
                  "AI interviews",
                  "Human + AI co-pilot",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2.5">
                    <ShieldCheck size={13} className="text-zinc-400 flex-shrink-0" />
                    <span className="text-[13px] text-zinc-300" style={{ fontFamily: FONT }}>{f}</span>
                  </div>
                ))}
              </div>
              <div className="px-6 pb-6">
                <button
                  onClick={bookDemo}
                  className="w-full py-2.5 bg-white hover:bg-zinc-100 text-zinc-900 rounded-lg text-[13px] font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  style={{ fontFamily: FONT }}
                >
                  Book a demo <ArrowRight size={13} />
                </button>
              </div>
            </div>

            {/* Enterprise */}
            <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden flex flex-col">
              <div className="px-6 pt-6 pb-5 border-b border-zinc-100">
                <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-3" style={{ fontFamily: FONT }}>For organizations</p>
                <p className="text-2xl font-bold text-zinc-900 mb-4" style={{ fontFamily: FONT }}>Enterprise</p>
                <p className="text-[15px] font-semibold text-zinc-900" style={{ fontFamily: FONT }}>Tailored pricing</p>
                <p className="text-[11px] text-zinc-400 mt-1" style={{ fontFamily: FONT }}>Custom volume & usage</p>
              </div>
              <div className="px-6 py-5 flex flex-col gap-3 flex-1">
                {[
                  "Custom volume & usage",
                  "Dedicated support & training",
                  "SSO integration",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2.5">
                    <ShieldCheck size={13} className="text-zinc-400 flex-shrink-0" />
                    <span className="text-[13px] text-zinc-600" style={{ fontFamily: FONT }}>{f}</span>
                  </div>
                ))}
              </div>
              <div className="px-6 pb-6">
                <button
                  onClick={bookDemo}
                  className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-700 text-white rounded-lg text-[13px] font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  style={{ fontFamily: FONT }}
                >
                  Book a demo <ArrowRight size={13} />
                </button>
              </div>
            </div>

          </div>

          <p className="text-center text-[12px] text-zinc-400 mt-8" style={{ fontFamily: FONT }}>
            All plans include the Agentic ATS · No credit card required to start
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-20 bg-zinc-900 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="flex flex-col items-center text-center py-14 gap-5">
            <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-500" style={{ fontFamily: FONT }}>
              Get Started
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-[56px] text-white font-semibold tracking-[-0.03em] leading-tight max-w-xl"
              style={{ fontFamily: FONT }}
            >
              Ready to transform your hiring?
            </h2>
            <div className="w-8 h-px bg-zinc-700" />
            <p className="text-zinc-400 text-[14px] leading-relaxed max-w-xs" style={{ fontFamily: FONT }}>
              No login or credit card required. See how Resolut works in minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
              <button
                onClick={() => navigate("/demo/ai-interview")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 rounded-lg font-semibold text-[13px] hover:bg-zinc-100 transition-colors cursor-pointer"
                style={{ fontFamily: FONT }}
              >
                Try AI Interview <ArrowRight size={14} />
              </button>
              <button
                onClick={bookDemo}
                className="inline-flex items-center gap-2 px-6 py-3 bg-transparent text-white rounded-lg font-semibold text-[13px] border border-zinc-700 hover:border-zinc-500 hover:bg-white/5 transition-colors cursor-pointer"
                style={{ fontFamily: FONT }}
              >
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <FreeTrialModal isOpen={showFreeTrialModal} onClose={() => setShowFreeTrialModal(false)} />
      <CalBookingModal isOpen={showCalModal} onClose={() => setShowCalModal(false)} />
    </div>
  )
}
